import { Gauge } from "lucide-react";

import { GrowthFeatureCard } from "./GrowthFeatureCard";
import {
  DEFAULT_GROWTH_FEATURE_SEO_AUDITS_COPY,
  formatSeoAuditMetricImproved,
  type GrowthFeatureSeoAuditsCardCopy,
  type SeoAuditsMetricIcon,
} from "./growth-feature-seo-audits-copy";

function MetricArrowIcon() {
  return (
    <svg
      className="cc-growth-features__seo-audits-metric-arrow"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M5 12h12M13 7l5 5-5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MetricIcon({ type }: { type: SeoAuditsMetricIcon }) {
  if (type === "speed") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="8" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M12 7v5l3 2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (type === "score") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M4 18l4-10 4 6 4-8 4 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (type === "traffic") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          d="M4 17v-4M9 17V9M14 17V6M19 17v-7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M16 16l4 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

type GrowthFeatureSeoAuditsProps = {
  copy?: GrowthFeatureSeoAuditsCardCopy;
};

export function GrowthFeatureSeoAudits({
  copy = DEFAULT_GROWTH_FEATURE_SEO_AUDITS_COPY,
}: GrowthFeatureSeoAuditsProps = {}) {
  const { demo } = copy;
  const metricsScroll = [...demo.metrics, ...demo.metrics];
  const auditsSummary = demo.metrics
    .map((metric) => formatSeoAuditMetricImproved(demo.metricImproved, metric))
    .join("; ");

  return (
    <GrowthFeatureCard
      title={copy.title}
      badge={copy.badge}
      icon={Gauge}
      description={copy.description}
    >
      <div className="cc-growth-features__seo-audits">
        <p className="cc-growth-features__seo-audits-sr-only">
          {demo.srOnlyIntro} {auditsSummary}.
        </p>
        <div className="cc-growth-features__seo-audits-panel" aria-hidden="true">
          <div className="cc-growth-features__seo-audits-surface">
            <div className="cc-growth-features__seo-audits-track">
              {metricsScroll.map((metric, index) => (
                <div
                  key={`${metric.label}-${index}`}
                  className="cc-growth-features__seo-audits-metric"
                >
                  <div className="cc-growth-features__seo-audits-metric-body">
                    <span className="cc-growth-features__seo-audits-metric-label">
                      {metric.label}
                    </span>
                    <div className="cc-growth-features__seo-audits-metric-values">
                      <span className="cc-growth-features__seo-audits-metric-before">
                        {metric.before}
                      </span>
                      <MetricArrowIcon />
                      <span className="cc-growth-features__seo-audits-metric-after">
                        {metric.after}
                      </span>
                    </div>
                  </div>
                  <span className="cc-growth-features__seo-audits-metric-icon">
                    <MetricIcon type={metric.icon} />
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </GrowthFeatureCard>
  );
}
